import React, { useState, FormEvent, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import logoImage from '../assets/image.png';
import { useTheme } from './components/ThemeContext';

interface LoginPageProps {
    onLogin: () => void;
    isSignUp?: boolean;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin, isSignUp = false }) => {
    const [username, setUsername] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const { theme } = useTheme();

    const handleLogin = (e: FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        setIsLoading(true);
        // Simulating login/signup process
        setTimeout(() => {
            setIsLoading(false);
            onLogin();
            navigate('/');
        }, 1000);
    };

    return (
        <div className={`min-h-screen flex items-center justify-center p-4 transition-colors duration-300 ${theme === 'dark'
                ? 'bg-gradient-to-br from-[#0f1419] via-[#16181d] to-[#1e2128]'
                : 'bg-gradient-to-r from-gray-200 to-gray-400'
            }`}>
            <div className={`max-w-md w-full space-y-8 p-10 rounded-2xl shadow-2xl transform transition-all duration-500 ease-in-out hover:scale-[1.02] ${theme === 'dark'
                    ? 'bg-[#252830] shadow-black/40 border border-[#30363d]'
                    : 'bg-white shadow-xl'
                }`}>
                <div className="text-center">
                    <div className="mx-auto w-24 h-24 flex items-center justify-center mb-4">
                        <img src={logoImage} alt="Kanun Kranti Logo" className={`max-w-full max-h-full object-contain rounded-full border-2 ${theme === 'dark' ? 'border-gray-600' : 'border-black'}`} />
                    </div>
                    <h2 className={`mt-6 text-3xl font-extrabold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}> VerdictAI</h2>
                    <p className={`mt-2 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{isSignUp ? 'Create your account' : 'AI-Driven Legal Research Engine'}</p>
                </div>
                <form className="mt-8 space-y-6" onSubmit={handleLogin}>
                    <div className="rounded-md shadow-sm -space-y-px">
                        <div>
                            <label htmlFor="username" className="sr-only">Username</label>
                            <input
                                id="username"
                                name="username"
                                type="text"
                                className={`appearance-none rounded-t-md relative block w-full px-3 py-3 border focus:outline-none focus:ring-2 focus:z-10 sm:text-sm transition-colors ${theme === 'dark'
                                        ? 'bg-[#1e2128] border-[#30363d] text-white placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500'
                                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-indigo-500 focus:border-indigo-500'
                                    }`}
                                placeholder="Username"
                                value={username}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setUsername(e.target.value)}
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="sr-only">Password</label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                className={`appearance-none rounded-b-md relative block w-full px-3 py-3 border focus:outline-none focus:ring-2 focus:z-10 sm:text-sm transition-colors ${theme === 'dark'
                                        ? 'bg-[#1e2128] border-[#30363d] text-white placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500'
                                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-indigo-500 focus:border-indigo-500'
                                    }`}
                                placeholder="Password"
                                value={password}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                            />
                        </div>
                    </div>

                    <div>
                        {!isSignUp && (
                            <button
                                type="button"
                                onClick={() => {
                                    onLogin();
                                    navigate('/');
                                }}
                                className={`group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-lg transition-all duration-200 mb-4 ${theme === 'dark'
                                        ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 hover:text-white'
                                        : 'bg-gray-500 text-white hover:bg-gray-600'
                                    }`}
                            >
                                Skip (Guest Access)
                            </button>
                        )}
                        <button
                            type="submit"
                            className={`group relative w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-medium rounded-lg text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${theme === 'dark'
                                    ? 'bg-blue-600 hover:bg-blue-500 focus:ring-blue-500 shadow-lg shadow-blue-500/20'
                                    : 'bg-[#302A2A] hover:bg-[#0b0b0b] focus:ring-amber-500'
                                } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : null}
                            {isLoading ? (isSignUp ? 'Creating Account...' : 'Logging in...') : (isSignUp ? 'Sign Up' : 'Log in')}
                        </button>

                    </div>
                </form>

                <div className={`mt-6 border-l-4 p-4 rounded-r-lg ${theme === 'dark'
                        ? 'bg-yellow-900/20 border-yellow-600 text-yellow-200'
                        : 'bg-yellow-100 border-yellow-500 text-[#302A2A]'
                    }`} role="alert">
                    <p className="font-bold">Note</p>
                    <p className="text-sm mt-1 opacity-90">The Login is currently dummy and the user can just Login by clicking Login button, Login IDs for judges and judiciary officers will be created using their official credentials once the platform is in production.</p>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
